import { type ReactElement, type ReactNode, memo } from "react";
import "Components/PlaybackLayout/HistoricalTimelineToolbar/HistoricalTimelineToolbar.less";
import {
	Button, IconWeight, type IconType, type SelectOption, Popover, PopoverPlacement, PopoverTrigger, Select
} from "@clintonelec/react-storybook";
import { ZoomLevel } from "Interfaces";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import {
	selectClipModeActive, selectDate, selectZoomLevel, setClipModeActiveAction, setZoomLevelAction
} from "Data/Redux/Slices/Playback";

const zoomOptions: SelectOption[] = [
	{
		label: "60 Minutes",
		value: ZoomLevel.SIXTY_MINUTES
	},
	{
		label: "30 Minutes",
		value: ZoomLevel.THIRTY_MINUTES
	},
	{
		label: "15 Minutes",
		value: ZoomLevel.FIFTEEN_MINUTES
	},
	{
		label: "5 Minutes",
		value: ZoomLevel.FIVE_MINUTES
	},
	{
		label: "1 Minute",
		value: ZoomLevel.ONE_MINUTE
	}
];

const timelineInfo: ReactElement = (
	<div className="timeline-info">
		<div>Recording Type:</div>
		<div className="recording-types">
			<span><span className="continuous" />Continuous</span>
			<span><span className="alarm" />Alarm</span>
			<span><span className="motion" />Motion</span>
			<span><span className="network-loss" />Network Loss</span>
			<span><span className="manual" />Manual</span>
		</div>
	</div>
);

function HistoricalTimelineToolbar() {
	const dispatch = useAppDispatch();
	const clipModeActive = useAppSelector(selectClipModeActive);
	const zoomLevel = useAppSelector(selectZoomLevel);
	const timelineDate = useAppSelector(selectDate);
	const changeZoomLevel = (value: ZoomLevel) => dispatch(setZoomLevelAction(value));
	const toggleClipModeActive = () => dispatch(setClipModeActiveAction(!clipModeActive));

	const handleZoomLevelChange = (value: string | null) => {
		if (!value) {
			return;
		}

		changeZoomLevel(Number(value));
	};

	const renderButton = (
		icon: string, popoverContent?: ReactNode, handleClick?: () => void, disabled?: boolean
	) => {
		const buttonIcon: IconType = {
			name: icon,
			weight: IconWeight.REGULAR
		};

		const content = <Button
			className="toolbar-button"
			icon={ buttonIcon }
			onClick={ handleClick }
			disabled={ disabled }
		/>;

		if (!popoverContent) {
			return content;
		}

		return (
			<Popover
				content={ popoverContent }
				interactive={ false }
				placement={ PopoverPlacement.TOP }
				trigger={ PopoverTrigger.CLICK }
			>
				{ content }
			</Popover>
		);
	};

	return (
		<div className="historical-timeline-toolbar">
			<div className="zoom-level-picker">
				<span>Timeline</span>
				<Select
					options={ zoomOptions }
					value={ zoomLevel }
					onSelect={ handleZoomLevelChange }
					allowClear={ false }
					menuPlacement="top"
					disabled={ clipModeActive }
				/>
			</div>
			<div className="toolbar-controls">
				<div className="center-content">
					{ renderButton("play") }
					{ renderButton("camera") }
					{ renderButton("expand") }
					{ renderButton("download", null, toggleClipModeActive, !timelineDate) }
					{ renderButton("square-info", timelineInfo) }
				</div>
			</div>
			<div className="right-content" />
		</div>
	);
}

export default memo(HistoricalTimelineToolbar);
